/**
 * FAQ Data Structure
 * Centralized FAQ items with categories for the dedicated FAQ page
 */

export const FAQ_ITEMS = [
  // Getting started
  {
    id: "what-is-optra",
    question: "What is OPTRA Labs?",
    answer: "OPTRA Labs is an AI-assisted platform that helps businesses identify relevant grants and funding pathways, assess eligibility, spot gaps, and prepare structured application content. We do not submit applications on your behalf; we help your team submit with stronger readiness.",
    category: "Getting started"
  },
  {
    id: "how-long-eligibility",
    question: "How long does the eligibility assessment take?",
    answer: "It is a short set of structured questions about your company, project and funding needs, and most teams finish it in one sitting. You'll get a grant fit summary with scores and rationale.",
    category: "Getting started"
  },
  {
    id: "account-required",
    question: "Do I need to create an account to try it?",
    answer: "You can browse public pages without an account. The Eligibility Assessment and the Full Support call are both available from the Pricing section.",
    category: "Getting started"
  },
  {
    id: "approval-guarantee",
    question: "Does OPTRA Labs guarantee grant approval?",
    answer: "No. OPTRA Labs provides guidance and structured drafts; final decisions rest with the grant authorities. Use the reviewer to catch common gaps before submission.",
    category: "Getting started"
  },

  // Features & workflow
  {
    id: "main-tools",
    question: "What are the main tools?",
    answer: "Our core workflow includes Eligibility Assessment, Application Composer, Grant Compliance Review, and Grant Roadmap planning support. Together they help you move from initial fit-check to submission-ready execution.",
    category: "Features & workflow"
  },
  {
    id: "uploads-formats",
    question: "Can I upload documents? What formats are supported?",
    answer: "Yes. Right now we support PDF uploads for best accuracy. We're actively expanding to additional formats; in the meantime, export or print to PDF for the cleanest results. Very large files may be trimmed, so keep core facts concise.",
    category: "Features & workflow"
  },
  {
    id: "collaboration",
    question: "Can I collaborate with teammates or my advisor?",
    answer: "Export a clean PDF and share it internally. Team seats and shared workspaces are on the roadmap; contact us for early access.",
    category: "Features & workflow"
  },
  {
    id: "submission",
    question: "Does OPTRA Labs submit applications for me?",
    answer: "No. OPTRA Labs prepares your content and checks consistency; submission happens in the relevant government portal or with your advisor.",
    category: "Features & workflow"
  },

  // Data & security
  {
    id: "model-training",
    question: "Do you train third-party models on my uploads?",
    answer: "No. Your uploads are processed to generate outputs and vector embeddings for retrieval inside OPTRA Labs. We do not sell your data or train public third-party models on your content.",
    category: "Data & security"
  },
  {
    id: "data-location",
    question: "Where is my data stored?",
    answer: "We use reputable cloud providers and store retrieval embeddings in a secure vector database. Data may be processed outside Singapore with safeguards that meet PDPA standards; see the Privacy Policy for details.",
    category: "Data & security"
  },
  {
    id: "data-deletion",
    question: "Can I request data deletion?",
    answer: "Yes. Email contact@optra-labs.com from your account email. We'll remove active records and schedule backup purges where applicable, subject to legal holds.",
    category: "Data & security"
  },
  {
    id: "compliance",
    question: "Is OPTRA Labs PDPA/GDPR compliant?",
    answer: "We follow PDPA requirements and include GDPR/CCPA-style rights where applicable. See our Privacy & Data Protection Policy for full details.",
    category: "Data & security"
  },

  // Pricing & billing
  {
    id: "billing",
    question: "How does pricing and payment work?",
    answer: "There are two options. The Eligibility Assessment is a one-time, self-serve purchase: the button in the Pricing section opens a secure Stripe checkout, where the amount is shown before you pay. Full Support starts with a free 15-minute call, after which we define scope, timeline and fees before any engagement begins.",
    category: "Pricing & billing"
  },
  {
    id: "cancellation-refunds",
    question: "Can I get a refund?",
    answer: "As set out in our Terms of Service, fees are non-refundable once paid, except at our discretion in the limited cases the Terms describe. If you believe you were charged in error, email contact@optra-labs.com and we will look into it.",
    category: "Pricing & billing"
  },
  {
    id: "trials-betas",
    question: "Do you offer trials or beta access?",
    answer: "We offer structured pilot and evaluation paths depending on business needs and use case complexity. Contact us if you need a custom enterprise evaluation flow.",
    category: "Pricing & billing"
  },

  // Grants coverage
  {
    id: "which-grants",
    question: "Which grants does OPTRA Labs cover today?",
    answer: "We currently support major Singapore-focused grant scenarios (including PSG, EDG, and SFEC references) and are continuously expanding coverage.",
    category: "Grants coverage"
  },
  {
    id: "non-sg",
    question: "Can OPTRA Labs advise on non-Singapore grants?",
    answer: "Not yet. If you operate in multiple markets, you can still use OPTRA Labs to structure internal content, but eligibility scoring is currently local to SG.",
    category: "Grants coverage"
  },

  // Support & legal
  {
    id: "contact",
    question: "How do I contact support?",
    answer: "Email contact@optra-labs.com. We aim to reply within 1–2 business days.",
    category: "Support & legal"
  },
  {
    id: "policies",
    question: "Where can I read your Privacy Policy and Terms?",
    answer: "They're linked in the site footer. Privacy explains data handling (including embeddings); Terms cover acceptable use and limitations.",
    category: "Support & legal"
  }
];

/**
 * Country wording. The questions, their ids and their order are the same in every region
 * (FAQ_ITEMS above is the Singapore wording); an entry here replaces only the fields that name
 * a country, its laws, its programs or its product flow. Three of the eight questions shown on the
 * homepage describe the product and its pricing, so they are reworded here for the U.S. product
 * (a one-time $200 setup fee that covers the first month, then $39 / $79 / $129 a month by tier,
 * with one assessment every two weeks;
 * sign in after checkout). U.S. program names come from the U.S. research docs; see
 * src/regions/us/CONTENT_REVIEW.md.
 */
const FAQ_COUNTRY_OVERRIDES = {
  US: {
    "account-required": {
      answer: "You can browse public pages without an account. Running the eligibility assessment needs one of the plans in the Pricing section: your account is set up at checkout, then you sign in to run it. The Full Support call is booked from the Pricing section.",
    },
    "main-tools": {
      answer: "Our core workflow starts with the Eligibility Assessment, which screens your business against federal notices, SBIR/STTR, state programs, and federal and state tax incentives. Full Support adds hands-on guidance from our team through the funding process.",
    },
    billing: {
      answer: "Every tier starts with a one-time $200 setup fee, which covers your first month. After that you pay your tier's monthly price: $39/month for Federal, $79/month for Federal + State, or $129/month for Federal + State + Tax credits. Every tier includes one eligibility assessment every two weeks: after you run one, the next becomes available 14 days later. The tier sets the scope. You can upgrade to a higher tier any time from Plan & Billing in the app: you move to the higher monthly price straight away and pay only the prorated difference for the rest of that billing month (nothing extra in your first month), with no second setup fee. The upgrade gives you one fresh assessment right away. You can also move to a lower tier at any time; the change takes effect at the end of your current billing month. You can cancel any time from the billing portal link in the app. Full Support starts with a free 15-minute call, after which we define scope, timeline and fees before any engagement begins.",
    },
    "cancellation-refunds": {
      question: "How do I cancel?",
      answer: "Cancel your monthly plan any time from Plan & Billing in the app, in one click. You keep access until the end of the month you have paid for, your saved assessments stay available, and you can resubscribe later at your tier's monthly price without paying the setup fee again. If you believe you were charged in error, email contact@optra-labs.com and we will look into it.",
    },
    "data-location": {
      answer: "We use reputable cloud providers and store retrieval embeddings in a secure vector database. See the Privacy Policy for where data is processed and the safeguards that apply.",
    },
    compliance: {
      question: "How does OPTRA Labs handle data protection compliance?",
      answer: "We include GDPR/CCPA-style rights where applicable. See our Privacy & Data Protection Policy for full details.",
    },
    "which-grants": {
      answer: "We currently support major U.S.-focused funding scenarios (including federal notices on Grants.gov, SBIR/STTR, state programs, and federal and state tax incentives) and are continuously expanding coverage.",
    },
    "non-sg": {
      question: "Can OPTRA Labs advise on grants outside the United States?",
      answer: "Not on this site. If you operate in multiple markets, you can still use OPTRA Labs to structure internal content, but eligibility scoring here is local to the U.S.",
    },
  },
};

/** FAQ items for a country: same ids and order everywhere, country-specific wording swapped in. */
export function getFaqItems(countryCode = "SG") {
  const overrides = FAQ_COUNTRY_OVERRIDES[countryCode];
  if (!overrides) return FAQ_ITEMS;
  return FAQ_ITEMS.map((item) => (overrides[item.id] ? { ...item, ...overrides[item.id] } : item));
}

export const FAQ_CATEGORIES = [
  "All",
  "Getting started",
  "Features & workflow",
  "Data & security",
  "Pricing & billing",
  "Grants coverage",
  "Support & legal"
];
